<script setup>
/* global axios */
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import countries from 'shared/constants/countries';
import { exportReportToPdf } from '../helpers/reportPdf';

const countryName = code => {
  if (!code) return '';
  const match = countries.find(c => c.id === code);
  return match ? match.name : code;
};

const props = defineProps({
  from: { type: Number, default: 0 },
  to: { type: Number, default: 0 },
});

const route = useRoute();
const accountId = route.params.accountId;

const STORAGE_KEY = 'csat_list_report_columns';

const FIXED_COLUMNS = [
  { key: 'conversation_id', label: 'Conversation ID', type: 'link' },
  { key: 'contact_name', label: 'Contact', get: r => r.contact?.name },
  { key: 'rating', label: 'CSAT Rating', type: 'rating' },
  { key: 'feedback', label: 'Feedback', get: r => r.feedback },
  { key: 'agent_name', label: 'Agent' },
  { key: 'solved_by_ai', label: 'Solved By AI', get: r => (r.solved_by_ai ? 'Yes' : 'No') },
  { key: 'created_at', label: 'Date', type: 'date' },
  { key: 'contact_email', label: 'Email', get: r => r.contact?.email },
  { key: 'contact_phone', label: 'Phone', get: r => r.contact?.phone_number },
  { key: 'company_domain', label: 'Company Domain', get: r => r.contact?.company_domain },
  { key: 'company_name', label: 'Company Name', get: r => r.contact?.company_name },
  { key: 'city', label: 'City', get: r => r.contact?.city },
  { key: 'country', label: 'Country', get: r => r.contact?.country || countryName(r.contact?.country_code) },
  { key: 'country_code', label: 'Country Code', get: r => r.contact?.country_code },
];

const DEFAULT_VISIBLE = [
  'conversation_id',
  'contact_name',
  'rating',
  'feedback',
  'agent_name',
];

const rows = ref([]);
const total = ref(0);
const summary = ref(null);
const page = ref(1);
const perPage = ref(25);
const loading = ref(false);
const showColumnPicker = ref(false);
const columnPickerRef = ref(null);
const visibleColumns = ref([...DEFAULT_VISIBLE]);

onClickOutside(columnPickerRef, () => {
  showColumnPicker.value = false;
});

onMounted(() => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(stored) && stored.length) visibleColumns.value = stored;
  } catch (_) {
    visibleColumns.value = [...DEFAULT_VISIBLE];
  }
});

const customAttrKeys = computed(() => {
  const set = new Set();
  rows.value.forEach(r =>
    Object.keys(r.contact?.custom_attributes || {}).forEach(k => set.add(k))
  );
  return [...set];
});

const allColumns = computed(() => [
  ...FIXED_COLUMNS,
  ...customAttrKeys.value.map(k => ({
    key: `custom:${k}`,
    label: k,
    get: r => r.contact?.custom_attributes?.[k],
  })),
]);

const activeColumns = computed(() =>
  visibleColumns.value
    .map(key => allColumns.value.find(c => c.key === key))
    .filter(Boolean)
);

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)));

// ---- Filters ----------------------------------------------------------------
const TEXT_FILTER_FIELDS = [
  { key: 'contact_name', label: 'Contact' },
  { key: 'agent_name', label: 'Agent' },
  { key: 'solved_by_ai', label: 'Solved By AI (yes/no)' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'contact_email', label: 'Email' },
  { key: 'contact_phone', label: 'Phone' },
  { key: 'company_domain', label: 'Company Domain' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'country_code', label: 'Country Code' },
];
const METRIC_FILTER_FIELDS = [
  { key: 'rating', label: 'CSAT Rating (1-5)', unit: 'count' },
];

const filters = ref([]);
const showFilterPanel = ref(false);
const filterPanelRef = ref(null);
onClickOutside(filterPanelRef, () => {
  showFilterPanel.value = false;
});

const filterFields = computed(() => [
  ...TEXT_FILTER_FIELDS,
  ...METRIC_FILTER_FIELDS,
  ...customAttrKeys.value.map(k => ({ key: `custom:${k}`, label: k })),
]);

const isMetric = key => METRIC_FILTER_FIELDS.some(f => f.key === key);

const addFilter = () => {
  filters.value.push({ field: '', value: '', min: '', max: '' });
};

const buildFilterPayload = () =>
  filters.value
    .map(f => {
      if (!f.field) return null;
      if (isMetric(f.field)) {
        const min = f.min !== '' && f.min != null ? Number(f.min) : undefined;
        const max = f.max !== '' && f.max != null ? Number(f.max) : undefined;
        if (min === undefined && max === undefined) return null;
        return { field: f.field, min, max };
      }
      if (!f.value) return null;
      return { field: f.field, value: f.value };
    })
    .filter(Boolean);

const appliedFilterCount = ref(0);

const applyFilters = () => {
  appliedFilterCount.value = buildFilterPayload().length;
  page.value = 1;
  fetchData();
  showFilterPanel.value = false;
};

const removeFilter = index => {
  filters.value.splice(index, 1);
};

const clearFilters = () => {
  filters.value = [];
  appliedFilterCount.value = 0;
  page.value = 1;
  fetchData();
};

const formatDate = epoch => {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toLocaleString();
};

const ratingText = r => (r === null || r === undefined ? '—' : `${r} / 5`);

const cellValue = (col, row) => {
  if (col.type === 'rating') return ratingText(row.rating);
  if (col.type === 'date') return formatDate(row.created_at);
  const raw = col.get ? col.get(row) : row[col.key];
  return raw === null || raw === undefined || raw === '' ? '—' : raw;
};

const toggleColumn = key => {
  if (visibleColumns.value.includes(key)) {
    visibleColumns.value = visibleColumns.value.filter(k => k !== key);
  } else {
    visibleColumns.value = [...visibleColumns.value, key];
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns.value));
};

const fetchData = async () => {
  if (!props.from || !props.to) return;
  loading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/csat_list`,
      {
        params: {
          since: props.from,
          until: props.to,
          page: page.value,
          filters: JSON.stringify(buildFilterPayload()),
        },
      }
    );
    rows.value = data.payload || [];
    total.value = data.meta?.count || 0;
    perPage.value = data.meta?.per_page || 25;
    summary.value = data.summary || null;
  } catch (_) {
    rows.value = [];
    total.value = 0;
    summary.value = null;
    useAlert('Could not load the CSAT list report.');
  } finally {
    loading.value = false;
  }
};

const exporting = ref(false);

const csvCellValue = (col, row) => {
  if (col.type === 'link') return row.conversation_id;
  if (col.type === 'rating') return row.rating ?? '';
  if (col.type === 'date') return row.created_at ? formatDate(row.created_at) : '';
  const raw = col.get ? col.get(row) : row[col.key];
  return raw === null || raw === undefined ? '' : raw;
};

const exportCsv = async () => {
  if (!props.from || !props.to) return;
  exporting.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/csat_list`,
      {
        params: {
          since: props.from,
          until: props.to,
          page: 1,
          per_page: 10000,
          filters: JSON.stringify(buildFilterPayload()),
        },
      }
    );
    const exportRows = data.payload || [];
    const cols = activeColumns.value;
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = cols.map(c => esc(c.label)).join(',');
    const body = exportRows
      .map(r => cols.map(c => esc(csvCellValue(c, r))).join(','))
      .join('\n');
    const csv = `﻿${header}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `csat-list-report-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (_) {
    useAlert('Could not export the report.');
  } finally {
    exporting.value = false;
  }
};

const pdfExporting = ref(false);

const dateRangeLabel = () => {
  if (!props.from || !props.to) return '';
  const f = new Date(props.from * 1000).toLocaleDateString();
  const t = new Date(props.to * 1000).toLocaleDateString();
  return `${f} – ${t}`;
};

const summaryTiles = () => {
  const s = summary.value;
  if (!s) return [];
  return [
    { label: 'AI (Captain) avg', value: s.ai_avg != null ? `${s.ai_avg} / 5` : '—' },
    { label: 'AI (Captain) responses', value: s.ai_count },
    { label: 'Overall avg', value: s.overall_avg != null ? `${s.overall_avg} / 5` : '—' },
    { label: 'Total responses', value: s.total },
  ];
};

const exportPdf = async () => {
  if (!props.from || !props.to) return;
  pdfExporting.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/csat_list`,
      {
        params: {
          since: props.from,
          until: props.to,
          page: 1,
          per_page: 10000,
          filters: JSON.stringify(buildFilterPayload()),
        },
      }
    );
    const exportRows = data.payload || [];
    const cols = activeColumns.value;
    exportReportToPdf({
      title: 'CSAT Report',
      dateRange: dateRangeLabel(),
      summary: summaryTiles(),
      headers: cols.map(c => c.label),
      rows: exportRows.map(r => cols.map(c => csvCellValue(c, r))),
    });
  } catch (_) {
    useAlert('Could not export the PDF.');
  } finally {
    pdfExporting.value = false;
  }
};

const goToPage = p => {
  if (p < 1 || p > totalPages.value || p === page.value) return;
  page.value = p;
  fetchData();
};

watch(
  () => [props.from, props.to],
  () => {
    page.value = 1;
    fetchData();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-3 py-4">
    <div class="flex items-center justify-between">
      <span class="text-sm text-n-slate-11">{{ total }} responses</span>
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50"
          :disabled="exporting"
          @click="exportCsv"
        >
          <span class="i-lucide-download size-4" />
          {{ exporting ? 'Exporting…' : 'CSV' }}
        </button>
        <button
          class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50"
          :disabled="pdfExporting"
          @click="exportPdf"
        >
          <span class="i-lucide-file-text size-4" />
          {{ pdfExporting ? 'Preparing…' : 'PDF' }}
        </button>
        <div ref="filterPanelRef" class="relative">
          <button
            class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
            @click="showFilterPanel = !showFilterPanel"
          >
            <span class="i-lucide-filter size-4" />
            Filters
            <span
              v-if="appliedFilterCount"
              class="ml-1 px-1.5 rounded-full bg-n-blue-9 text-white text-xs"
            >{{ appliedFilterCount }}</span>
          </button>
          <div
            v-if="showFilterPanel"
            class="absolute right-0 z-30 mt-1 w-[420px] max-h-[30rem] overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-3"
          >
            <p class="text-xs uppercase font-semibold text-n-slate-10 mb-2">
              Filter CSAT responses
            </p>
            <div
              v-for="(f, i) in filters"
              :key="i"
              class="mb-2 p-2 rounded-lg border border-n-weak"
            >
              <div class="flex items-center gap-2">
                <select
                  v-model="f.field"
                  class="flex-1 min-w-0 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Select field…</option>
                  <option v-for="ff in filterFields" :key="ff.key" :value="ff.key">
                    {{ ff.label }}
                  </option>
                </select>
                <button
                  class="shrink-0 text-n-slate-10 hover:text-n-slate-12 px-1"
                  @click="removeFilter(i)"
                >
                  ✕
                </button>
              </div>
              <div v-if="f.field" class="mt-2">
                <div v-if="isMetric(f.field)" class="flex items-center gap-2">
                  <input
                    v-model="f.min"
                    type="number"
                    placeholder="min"
                    class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                  />
                  <span class="text-n-slate-10 text-sm">to</span>
                  <input
                    v-model="f.max"
                    type="number"
                    placeholder="max"
                    class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                  />
                </div>
                <input
                  v-else
                  v-model="f.value"
                  type="text"
                  placeholder="contains…"
                  class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </div>
            </div>
            <button
              class="mt-1 text-sm text-n-blue-text hover:underline"
              @click="addFilter"
            >
              + Add filter
            </button>
            <div class="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-n-weak">
              <button
                class="px-3 py-1.5 text-sm rounded-lg text-n-slate-11 hover:bg-n-slate-3"
                @click="clearFilters"
              >
                Clear
              </button>
              <button
                class="px-4 py-1.5 text-sm rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10"
                @click="applyFilters"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
        <div ref="columnPickerRef" class="relative">
          <button
            class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
            @click="showColumnPicker = !showColumnPicker"
          >
            <span class="i-lucide-columns-3 size-4" />
            Columns
          </button>
          <div
            v-if="showColumnPicker"
            class="absolute right-0 z-30 mt-1 w-64 max-h-96 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-2"
          >
            <p class="px-2 py-1 text-xs uppercase font-semibold text-n-slate-10">
              Show columns
            </p>
            <label
              v-for="col in allColumns"
              :key="col.key"
              class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-slate-3 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="visibleColumns.includes(col.key)"
                @change="toggleColumn(col.key)"
              />
              <span class="text-sm text-n-slate-12">{{ col.label }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="summary" class="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div class="rounded-xl border border-n-weak p-3">
        <p class="text-xs text-n-slate-10">AI (Captain) avg rating</p>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ summary.ai_avg != null ? `${summary.ai_avg} / 5` : '—' }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak p-3">
        <p class="text-xs text-n-slate-10">AI (Captain) responses</p>
        <p class="text-lg font-semibold text-n-slate-12">{{ summary.ai_count }}</p>
      </div>
      <div class="rounded-xl border border-n-weak p-3">
        <p class="text-xs text-n-slate-10">Overall avg rating</p>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ summary.overall_avg != null ? `${summary.overall_avg} / 5` : '—' }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak p-3">
        <p class="text-xs text-n-slate-10">Total responses</p>
        <p class="text-lg font-semibold text-n-slate-12">{{ summary.total }}</p>
      </div>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              v-for="col in activeColumns"
              :key="col.key"
              class="text-left font-semibold text-n-slate-11 px-4 py-3 whitespace-nowrap"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="activeColumns.length" class="px-4 py-8 text-center text-n-slate-10">
              Loading…
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="activeColumns.length" class="px-4 py-8 text-center text-n-slate-10">
              No CSAT responses in this range.
            </td>
          </tr>
          <tr
            v-for="row in rows"
            v-else
            :key="row.id"
            class="border-b border-n-weak last:border-b-0 hover:bg-n-slate-2"
          >
            <td
              v-for="col in activeColumns"
              :key="col.key"
              class="px-4 py-3 whitespace-nowrap text-n-slate-12"
            >
              <router-link
                v-if="col.type === 'link'"
                :to="{ name: 'inbox_conversation', params: { conversation_id: row.conversation_id } }"
                class="text-n-blue-text hover:underline"
              >
                #{{ row.conversation_id }}
              </router-link>
              <span v-else>{{ cellValue(col, row) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="total > 0" class="flex items-center justify-end gap-2 text-sm text-n-slate-11">
      <button
        class="px-2 py-1 rounded border border-n-weak disabled:opacity-40"
        :disabled="page <= 1"
        @click="goToPage(page - 1)"
      >
        ‹
      </button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button
        class="px-2 py-1 rounded border border-n-weak disabled:opacity-40"
        :disabled="page >= totalPages"
        @click="goToPage(page + 1)"
      >
        ›
      </button>
    </div>
  </div>
</template>
